import { yupResolver } from "@hookform/resolvers/yup";
import {
  Autocomplete,
  Box,
  FormControl,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import OfficeGroup from "assets/images/officeGroup.svg";
import { Button, Input } from "components";
import {
  addAttributeToIntercom,
  useCreateCustomer,
  useGetAllCountries,
  useGlobalStore,
} from "controller";
import React, { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { addMixpanelEvent, isDomainNameValid, isUrl } from "utilz/helpers";
import * as yup from "yup";

interface FormValues {
  customer_name: string;
  domain_name: string[];
  street: string | null;
  country: string | null;
  state: string | null;
  city: string | null;
}

const schema = yup.object({
  customer_name: yup
    .string()
    .trim()
    .required("Customer name is required")
    .min(2, "Customer name must be at least 2 characters")
    .max(100, "Customer name must be at most 100 characters")
    .matches(/^[^<>"\\%^/~*|]*$/, "Customer name contains invalid characters.")
    .matches(
      /^(?!.*\s\s).*$/,
      "Customer name cannot contain consecutive spaces",
    )
    .test(
      "is-not-url",
      "Customer name cannot be a URL",
      (value) => !isUrl(value || ""),
    ),
  domain_name: yup.array().of(yup.string()).default([]),
  street: yup
    .string()
    .max(100, "Street must be at most 100 characters")
    .nullable(),
  country: yup.string().nullable().required("Country is required"),
  state: yup.string().nullable(),
  city: yup
    .string()
    .nullable()
    .max(50, "City must be at most 50 characters")
    .matches(/\p{L}/u, {
      message: "City must contain at least one letter",
      excludeEmptyString: true,
    }),
});

function StepOneCreateCustomer({
  onClose,
  setCurrentStep,
  setNewCustomerMappingId,
}: {
  onClose: () => void;
  setCurrentStep: () => void;
  setNewCustomerMappingId: React.Dispatch<React.SetStateAction<any>>;
}) {
  const domainsTextFieldRef = useRef(null);
  const [domainNameTextFieldValue, setDomainNameTextFieldValue] = useState("");
  const resourceId = useGlobalStore((state) => state.resourceId);
  const mspCustomerData = useGlobalStore((state) => state.mspCountryData);
  const { mutateAsync: createCustomer, isLoading: isCreateCustomerLoading } =
    useCreateCustomer();

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      customer_name: "",
      domain_name: [],
      street: null,
      country: mspCustomerData?.countryCode || null,
      state: null,
      city: null,
    },
    resolver: yupResolver(schema),
  });

  const theme = useTheme();
  const watchDomains = watch("domain_name");

  const handleDomains = (
    value: string[] | unknown,
    onChange: (...event: any[]) => void,
  ) => {
    if (
      Array.isArray(value) &&
      value.every((item) => typeof item === "string")
    ) {
      const lastDomain = value[value.length - 1] || ""; // Safeguard for empty array
      if (!isDomainNameValid(lastDomain)) {
        setError("domain_name", {
          type: "customError",
          message: "Please enter a valid domain name",
        });
      } else {
        clearErrors("domain_name");
        setValue("domain_name", value);
        setDomainNameTextFieldValue("");
      }
    } else {
      console.error("Invalid value type passed to handleDomains");
    }
  };

  const onSubmit = async (data: any) => {
    const newCreateCustomerData = data;
    let isError = false;
    if (domainNameTextFieldValue) {
      if (!isDomainNameValid(domainNameTextFieldValue)) {
        setError("domain_name", {
          type: "customError",
          message: "Please enter a valid domain name",
        });
        isError = true;
      } else {
        clearErrors("domain_name");
        setValue("domain_name", [...watchDomains, domainNameTextFieldValue]);
        newCreateCustomerData.domain_name = [
          ...newCreateCustomerData.domain_name,
          domainNameTextFieldValue,
        ];
        setDomainNameTextFieldValue("");
      }
    }
    if (!isError) {
      try {
        const createCustomerData = await createCustomer(newCreateCustomerData);
        if (createCustomerData?.data?.mapping_id) {
          setNewCustomerMappingId(createCustomerData?.data);
          resourceId &&
            addAttributeToIntercom({
              event: "isCustomerAdded",
            });
          addMixpanelEvent({
            event: "create_customer",
          });
          toast.success("Customer created successfully");
          setCurrentStep();
        }
      } catch (error: any) {
        if (typeof error.message === "string") {
          toast.error(
            error.message ||
              "Unable to create customer at the moment. Try again later",
          );
        } else {
          toast.error(
            error?.response?.data?.message ||
              "Unable to create customer at the moment. Try again later",
          );
        }
      }
    }
  };

  const onError = (errors: any) => {
    if (domainNameTextFieldValue) {
      if (!isDomainNameValid(domainNameTextFieldValue)) {
        setError("domain_name", {
          type: "customError",
          message: "Please enter a valid domain name",
        });
      } else {
        clearErrors("domain_name");
        setDomainNameTextFieldValue("");
        setValue("domain_name", [...watchDomains, domainNameTextFieldValue]);
      }
    }
  };

  const onSubmitHandler = handleSubmit(onSubmit, onError);

  return (
    <Stack width="100%" flex={1}>
      <Stack
        sx={{
          display: "flex",
          justifyContent: "center",
          width: "100%",
          px: "32px",
          gap: "8px",
        }}
      >
        <Typography variant="h4">Add a customer</Typography>
        <Typography variant="body-medium">
          Create a customer account to start managing tickets, users, and
          services.
        </Typography>
      </Stack>
      <Stack
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: "32px",
          // background:
          //   "radial-gradient(68.86% 50.52% at 51.07% 49.48%, #F4ECFF 0%, #FFFFFF 100%)",
        }}
      >
        <img
          src={OfficeGroup}
          style={{
            width: "355px",
            height: "auto",
            transition: "opacity 0.3s ease-in-out",
            opacity: 1,
          }}
          alt="Office Group"
        />
        <Stack gap="20px" sx={{ width: "100%" }}>
          <Input
            control={control}
            name="customer_name"
            required={true}
            label="Customer Name"
            error={!!errors.customer_name}
            errorMessage={errors?.customer_name?.message}
            placeholder="eg : Oceanix Pvt.Ltd."
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: "transparent",
              },
            }}
          />
          <Stack sx={{ marginBottom: "10px" }}>
            <FormControl>
              <Controller
                control={control}
                name={"domain_name"}
                render={({ field: { ref, value, onBlur, onChange, name } }) => (
                  <Autocomplete
                    ref={ref}
                    value={value || []}
                    onBlur={onBlur}
                    slotProps={{
                      popper: {
                        sx: { boxShadow: "none" },
                      },
                      paper: {
                        sx: {
                          mt: "10px",
                          backgroundColor: "#FFF",
                          borderRadius: "8px",
                          boxShadow:
                            "0px 0px 8px 0px rgba(0, 0, 0, 0.06), 0px 0px 2px 0px rgba(0, 0, 0, 0.08)",
                        },
                      },
                    }}
                    ChipProps={{
                      sx: {
                        height: "26px",
                        borderRadius: "4px",
                        padding: "4px 2px",
                        ...theme.typography["body-small"],
                      },
                    }}
                    multiple
                    fullWidth={true}
                    onChange={(event, newValue) => {
                      handleDomains(newValue, onChange);
                    }}
                    onKeyDown={(event: any) => {
                      if (event.key === "Enter") {
                        const newInputValue = event.target.value;
                        const isDuplicate =
                          watchDomains.includes(newInputValue);
                        if (isDuplicate) {
                          toast.error("Domain already added!");
                        }
                      }
                    }}
                    selectOnFocus
                    handleHomeEndKeys
                    id="free-solo-with-text-demo"
                    getOptionLabel={(option: any) => {
                      // Value selected with enter, right from the input
                      if (typeof option === "string") {
                        return option;
                      }
                      // Add "xxx" option created dynamically
                      if (option.inputValue) {
                        return option.inputValue;
                      }
                      // Regular option
                      return option.title;
                    }}
                    renderOption={(props, option: any) => (
                      <li {...props}>{option.title}</li>
                    )}
                    sx={{
                      width: "100%",
                      ...theme.typography["body-medium"],
                    }}
                    freeSolo
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        inputRef={domainsTextFieldRef}
                        label="Domains"
                        name="domain_name"
                        sx={{
                          "& .MuiInputBase-input": {
                            height: "15px",
                          },
                          "& .MuiOutlinedInput-root": {
                            color: "#1C1E26",
                            padding: 0,

                            "& > fieldset": {
                              borderColor: "#CCCCCC",
                              borderRadius: "4px",
                            },
                          },
                          "& .MuiOutlinedInput-root.Mui-focused": {
                            color: "#1C1E26",
                            "& > fieldset": { borderColor: "#5563F5" },
                          },
                          ".Mui-error > fieldset": {
                            borderColor: theme.palette.error.main,
                          },

                          "& .MuiInputLabel-root": {
                            color: errors.domain_name
                              ? theme.palette.error.main
                              : "#767B85",
                            fontSize: "14px",
                            transform:
                              value?.length > 0 || domainNameTextFieldValue
                                ? "translate(14px, -9px) scale(0.8)"
                                : "translate(10px, 5px) scale(1)",

                            "&.Mui-focused": {
                              color: errors.domain_name
                                ? theme.palette.error.main
                                : "#5563F5",
                              fontWeight: "700",
                              transform: "translate(14px, -9px) scale(1)",
                              fontSize: "12px",
                            },
                          },

                          "& .MuiAutocomplete-tag": {
                            backgroundColor: "#E7EBF3",
                          },
                        }}
                        type="text"
                        placeholder="Type and press enter to add more domains"
                        error={!!errors.domain_name}
                      />
                    )}
                    inputValue={domainNameTextFieldValue}
                    onInputChange={(e: any) =>
                      setDomainNameTextFieldValue(`${e.target.value || ""}`)
                    }
                    options={[]}
                  />
                )}
              />
              <Box sx={{ height: errors?.domain_name ? "6px" : "4px" }}>
                <Typography
                  variant="body-small"
                  color="error"
                  sx={{
                    ml: "8px",
                    mt: "12px",
                  }}
                >
                  {errors?.domain_name?.message}
                </Typography>
              </Box>
            </FormControl>
          </Stack>

          <Button
            className="intercom-addCustomer"
            variant="primary"
            onClick={() => {
              onSubmitHandler();
            }}
            loading={isCreateCustomerLoading}
          >
            Create Customer
          </Button>
          <Button
            className="intercom-addCustomer"
            variant="secondary"
            onClick={() => {
              onClose();
            }}
            disabled={isCreateCustomerLoading}
          >
            Cancel
          </Button>
        </Stack>
      </Stack>
      <LearnHowToAddANewCustomer />
    </Stack>
  );
}

const LearnHowToAddANewCustomer = () => (
  <Stack
    direction={"row"}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      mt: "auto",
    }}
  >
    <Typography
      variant="body-medium"
      color={"#767B85"}
      className="imageCardTitle"
    >
      Need help?
    </Typography>
    <Typography
      className="imageCardTitle"
      variant="body-medium"
      sx={{
        color: "#5563F5",
        textDecoration: "underline",
        cursor: "pointer",
      }}
      onClick={() => {
        window.open(
          "https://support.deskday.com/en/articles/8236110-adding-a-new-customer",
          "_blank",
        );
      }}
    >
      Learn how to add a new customer
    </Typography>
  </Stack>
);

export default StepOneCreateCustomer;
